import { Box, Button, Flex, Icon } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogCloseTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEffect, useState, useCallback } from 'react';
import { FiSend } from 'react-icons/fi';
import { RiQrCodeLine, RiThumbUpLine, RiUserLine } from 'react-icons/ri';
import { ChannelConnection } from './channel-step-connection';
import { ConfirmDetail } from './channel-step-confirm-detail';
import { FinishSettings } from './channel-step-finish-settings';
import { StartChannel } from './channel-step-start';
import { useWhatsAppChannel } from '@/hooks/WAHA/useWhatsapp';
import { Channel as ChannelInterface } from '@/features/channels/channels.types';
import { useWidgetPortalProps } from '@/features/widget/hooks/widget-modal-hooks';
import { extractPhoneNumber } from '@/services/interface';
import { useOrgLocation } from '@/context/organization-location-context';

interface AddWhatsAppChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChannelCreate: (channel: ChannelInterface) => void;
  selectedChannel?: ChannelInterface | null;
}

const steps = [
  { title: 'Start channel', icon: FiSend },
  { title: 'Channel Connection', icon: RiQrCodeLine },
  { title: 'Confirm Detail', icon: RiUserLine },
  { title: 'Finish Settings', icon: RiThumbUpLine },
];

export function AddWhatsAppChannelModal({
  isOpen,
  onClose,
  onChannelCreate,
  selectedChannel,
}: AddWhatsAppChannelModalProps) {
  const portalProps = useWidgetPortalProps();
  const [activeStep, setActiveStep] = useState(0);
  const [channelName, setChannelName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { selectedLocation, selectedOrganisation } = useOrgLocation();
  const [currentChannelId, setCurrentChannelId] = useState<string | undefined>(
    undefined,
  );
  const [tempSessionId, setTempSessionId] = useState<string | undefined>(
    undefined,
  );
  const [businessName, setBusinessName] = useState('');

  const whatsAppChannel = useWhatsAppChannel();

  // First, get the session statuses
  const { data: sessionStatus } =
    whatsAppChannel.useWhatsAppSessionStatus(currentChannelId);

  const { data: tempSessionStatus, isLoading: isLoadingTempSession } =
    whatsAppChannel.useWhatsAppSessionStatus(tempSessionId);

  // Then, use tempSessionStatus in the QR code hook
  const {
    data: qrCodeData,
    isLoading: isLoadingQR,
    error: qrError,
    refetch: refreshQR,
  } = whatsAppChannel.useWhatsAppQRCode(
    // Use currentChannelId for reconnection, tempSessionId for new channels
    selectedChannel ? currentChannelId : tempSessionId,
    // Only need QR if status is SCAN_QR_CODE
    (selectedChannel && sessionStatus?.status === 'SCAN_QR_CODE') ||
      (!selectedChannel && tempSessionStatus?.status === 'SCAN_QR_CODE'),
  );

  // Define connection flags
  const isConnected =
    sessionStatus?.status === 'WORKING' ||
    sessionStatus?.status === 'CONNECTED';
  const isTempConnected =
    tempSessionStatus?.status === 'WORKING' ||
    tempSessionStatus?.status === 'CONNECTED';

  useEffect(() => {
    if (qrCodeData) {
      setActiveStep(1);
    }
  }, [qrCodeData]);

  useEffect(() => {
    if (isOpen) {
      if (selectedChannel) {
        setChannelName(selectedChannel.name);
        setPhoneNumber(selectedChannel.phone || '');
        setCurrentChannelId(selectedChannel.id);

        // If reconnecting (SCAN_QR_CODE, etc.), jump straight to the connection step
        if (
          selectedChannel.status === 'SCAN_QR_CODE' ||
          selectedChannel.status === 'INITIALIZING' ||
          selectedChannel.status === 'PENDING' ||
          selectedChannel.status === 'FAILED'
        ) {
          setActiveStep(1);
          // The channel ID and session ID are the same
          setTempSessionId(undefined); // Reset any temp session
        }
      } else {
        // Reset when adding a new channel
        setChannelName('');
        setBusinessName('');
        setPhoneNumber('');
        setCurrentChannelId(undefined);
        setTempSessionId(undefined);
        setActiveStep(0);
      }
    }
  }, [isOpen, selectedChannel]);

  useEffect(() => {
    const updateChannel = async () => {
      if (
        sessionStatus?.status === 'WORKING' ||
        sessionStatus?.status === 'CONNECTED'
      ) {
        if (sessionStatus.me?.id) {
          const extractedPhone = extractPhoneNumber(sessionStatus.me.id);
          setPhoneNumber(extractedPhone);
          if (currentChannelId) {
            await whatsAppChannel
              .updateChannel({
                id: currentChannelId,
                data: {
                  phone: extractedPhone,
                  status: sessionStatus.status as
                    | 'WORKING'
                    | 'CONNECTED'
                    | 'FAILED'
                    | 'PENDING'
                    | 'SCAN_QR_CODE',
                  channel_config: {
                    businessName: channelName,
                    businessPhone: extractedPhone,
                    sessionId: currentChannelId,
                    lastConnected: new Date().toISOString(),
                  },
                },
              })
              .catch((err) => {
                console.error('Error updating channel with phone:', err);
              });
          }
        }
      }
      if (sessionStatus?.status === 'FAILED' && currentChannelId) {
        await whatsAppChannel
          .updateChannel({
            id: currentChannelId,
            data: { status: 'FAILED' },
          })
          .catch((err) => {
            console.error('Error updating channel status to failed:', err);
          });
      }
    };

    updateChannel();
  }, [sessionStatus, currentChannelId, channelName]);

  useEffect(() => {
    if (
      (tempSessionStatus?.status === 'WORKING' ||
        tempSessionStatus?.status === 'CONNECTED') &&
      !selectedChannel
    ) {
      if (tempSessionStatus.me?.id) {
        const extractedPhone = extractPhoneNumber(tempSessionStatus.me.id);
        setPhoneNumber(extractedPhone);
        const extractedName =
          tempSessionStatus.me?.pushName || `WhatsApp ${extractedPhone}`;
        setBusinessName(extractedName);
        setChannelName(extractedName);
        console.log(
          'WhatsApp connected successfully! Moving to confirmation step.',
        );
        setTempSessionId(tempSessionStatus.name);
      }
    }
  }, [tempSessionStatus, selectedChannel]);

  const handleStartTempSession = useCallback(async () => {
    try {
      const sessionId = await whatsAppChannel.createTemporarySession();
      setTempSessionId(sessionId);
    } catch (error) {
      console.error('Failed to create temporary session:', error);
    }
  }, [whatsAppChannel]);

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      if (selectedChannel?.id) {
        setCurrentChannelId(selectedChannel.id);
        setActiveStep(1);
      } else {
        await handleStartTempSession();
        setActiveStep(1);
      }
    } else if (activeStep === 1 && selectedChannel) {
      // For reconnection, once it's connected, go back to overview instead of proceeding through the wizard
      if (
        sessionStatus?.status === 'WORKING' ||
        sessionStatus?.status === 'CONNECTED'
      ) {
        // Channel already exists, just close the modal after reconnection
        handleCloseAndReset();

        // Update the channel with latest status if needed
        if (sessionStatus.me?.id) {
          const extractedPhone = extractPhoneNumber(sessionStatus.me.id);
          onChannelCreate({
            ...selectedChannel,
            phone: extractedPhone,
            status: sessionStatus.status as
              | 'WORKING'
              | 'CONNECTED'
              | 'FAILED'
              | 'PENDING'
              | 'SCAN_QR_CODE',
          });
        }
      }
    } else if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  }, [selectedChannel]);

  const handleFinish = async () => {
    try {
      if (tempSessionId && !selectedChannel) {
        const result = await whatsAppChannel.createChannel({
          sessionName: tempSessionId,
          channelName: channelName,
          phoneNumber: phoneNumber,
          locationId: selectedLocation?.id || '',
          organisationId: selectedOrganisation?.id || '',
        });
        onChannelCreate(result);

        // Move to final step
        setActiveStep(steps.length - 1);
      } else if (selectedChannel) {
        // Update existing channel
        onChannelCreate(selectedChannel);
        handleCloseAndReset();
      }
    } catch (error) {
      console.error('Failed to create channel:', error);
    }
  };

  const handlePrevious = () => {
    const prevStep = activeStep - 1;
    if (prevStep >= 0) {
      setActiveStep(prevStep);
    }
  };

  const handleCloseAndReset = () => {
    onClose();
    setActiveStep(0);
    setChannelName('');
    setPhoneNumber('');
    setCurrentChannelId(undefined);
    setTempSessionId(undefined);
  };

  const renderStepContent = () => {
    // Determine which step to show based on state
    const isReconnecting =
      selectedChannel &&
      (selectedChannel.status === 'SCAN_QR_CODE' ||
        selectedChannel.status === 'INITIALIZING' ||
        selectedChannel.status === 'PENDING' ||
        selectedChannel.status === 'FAILED');

    const needsQRScan =
      (selectedChannel && sessionStatus?.status === 'SCAN_QR_CODE') ||
      (!selectedChannel && tempSessionStatus?.status === 'SCAN_QR_CODE');

    const actualQRCode =
      needsQRScan && typeof qrCodeData === 'string' ? qrCodeData : undefined;

    switch (activeStep) {
      case 0:
        return (
          <StartChannel
            onStart={handleStartTempSession}
            isLoading={
              isLoadingQR ||
              whatsAppChannel.isCreatingTempSession ||
              isLoadingTempSession
            }
          />
        );
      case 1:
        return (
          <ChannelConnection
            isCreatingSession={whatsAppChannel.isCreatingTempSession}
            qrCodeData={actualQRCode}
            isLoadingQR={isLoadingQR}
            isConnected={isReconnecting ? isConnected : isTempConnected}
            isReconnecting={!!selectedChannel}
            onRefreshQR={refreshQR}
            sessionStatus={isReconnecting ? sessionStatus : tempSessionStatus}
            onConnectionSuccess={() => setActiveStep(2)}
          />
        );
      case 2:
        return (
          <ConfirmDetail
            channelName={channelName}
            onChannelNameChange={setChannelName}
            businessName={businessName}
            businessPhone={phoneNumber}
          />
        );
      case 3:
        return <FinishSettings onClose={handleCloseAndReset} />;
      default:
        return null;
    }
  };

  const isLastStepScreen = activeStep === steps.length - 1;
  const isConfirmDetailStep = activeStep === 2;

  useEffect(() => {
    return () => {
      handleCloseAndReset();
    };
  }, []);

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && handleCloseAndReset()}
      closeOnInteractOutside={false}
      {...portalProps}
    >
      <DialogContent maxW="900px">
        <DialogHeader>
          <DialogTitle>
            {selectedChannel
              ? 'Reconnect WhatsApp Channel'
              : 'Add WhatsApp Channel'}
          </DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody display="flex" flexDirection="column" overflow="auto">
          {/* Simple step indicator */}
          <Flex justify="center" mb={8}>
            {steps.map((step, index) => (
              <Flex key={index} align="center">
                <Box
                  w={8}
                  h={8}
                  borderRadius="full"
                  bg={index <= activeStep ? 'blue.500' : 'gray.200'}
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  <Icon as={step.icon} />
                </Box>
                <Box
                  ml={2}
                  fontSize="sm"
                  color={index <= activeStep ? 'blue.500' : 'gray.500'}
                >
                  {step.title}
                </Box>
                {index < steps.length - 1 && (
                  <Box w={8} h={0.5} bg="gray.200" mx={2} />
                )}
              </Flex>
            ))}
          </Flex>

          <Box flex="1" overflowY="auto" mb={8}>
            {renderStepContent()}
          </Box>

          {!isLastStepScreen && (
            <Flex justify="space-between" mt="auto">
              <Button
                onClick={handlePrevious}
                disabled={
                  activeStep === 0 || whatsAppChannel.isCreatingTempSession
                }
                variant="ghost"
              >
                Previous
              </Button>
              {isConfirmDetailStep ? (
                <Button
                  onClick={handleFinish}
                  disabled={!channelName || (!isConnected && !isTempConnected)}
                  colorPalette="green"
                >
                  Finish
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  loading={
                    whatsAppChannel.isCreatingTempSession ||
                    (activeStep === 1 && isLoadingQR && !qrCodeData)
                  }
                  disabled={
                    activeStep === 0 ||
                    (activeStep === 1 && !isTempConnected && !qrError) ||
                    whatsAppChannel.isCreatingTempSession
                  }
                  colorPalette="green"
                >
                  Next
                </Button>
              )}
            </Flex>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
